import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../lib/api';

// ============================================
// Types - Consolidated Report
// ============================================

export interface ConsolidatedReportSummary {
  totalInflow: number;
  totalOutflow: number;
  grossRevenue: number;
  revenueByType: { cashIn: number; cashOut: number; membership: number };
  operationalCosts: number;
  netProfit: number;
  profitMargin: number;
}

export interface TimeSeriesEntry {
  period: string;
  inflow: number;
  outflow: number;
  grossRevenue: number;
  costs: number;
  netProfit: number;
}

export interface ConsolidatedReportResponse {
  summary: ConsolidatedReportSummary;
  timeSeries: TimeSeriesEntry[];
  charts: any[];
}

export interface ConsolidatedReportFilters {
  startDate?: string;
  endDate?: string;
  groupBy?: 'day' | 'week' | 'month';
  operationType?: string;
}

// ============================================
// Types - Per Customer Report
// ============================================

export interface CustomerEntry {
  ranking: number;
  userName: string;
  userDocument: string;
  virtualAccountId: string;
  inflow: number;
  outflow: number;
  availableBalance: number;
  blockedBalance: number;
  cashInFeesPaid: number;
  membershipFeesPaid: number;
  transactionCount: number;
  contractStatus: string;
  kycOriginStatus: string;
}

export interface PerCustomerReportResponse {
  summary: {
    totalActiveCustomers: number;
    totalVolume: number;
    totalFeesCollected: number;
    avgTransactionsPerCustomer: number;
  };
  customers: CustomerEntry[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface PerCustomerReportFilters {
  startDate?: string;
  endDate?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  page?: number;
  limit?: number;
}

// ============================================
// Types - Fees & Membership Report
// ============================================

export interface MembershipByCompetenceEntry {
  competence: string;
  paidCount: number;
  paidAmount: number;
  pendingCount: number;
  pendingAmount: number;
  failedCount: number;
  failedAmount: number;
  delinquencyRate: number;
}

export interface FeesByTypeEntry {
  type: string;
  totalRevenue: number;
  chargeCount: number;
  averageCharge: number;
}

export interface RevenueProjection {
  activeAccounts: number;
  membershipValue: number;
  projectedMembership: number;
  avgDailyFees: number;
  projectedFees: number;
  totalProjected: number;
}

export interface FeesMembershipReportResponse {
  summary: {
    totalMembershipCollected: number;
    totalMembershipPending: number;
    delinquencyRate: number;
    transactionFeeRevenue: number;
    projectedMonthlyRevenue: number;
  };
  membershipByCompetence: MembershipByCompetenceEntry[];
  feesByType: FeesByTypeEntry[];
  projection: RevenueProjection;
  charts: any[];
}

export interface FeesMembershipReportFilters {
  competence?: string;
  chargeStatus?: string;
  startDate?: string;
  endDate?: string;
}

// ============================================
// Types - Detailed Transactions Report
// ============================================

export interface TransactionEntry {
  id: string;
  type: string;
  status: string;
  grossAmount: number;
  totalFees: number;
  netAmount: number;
  createdAt: string;
  processedAt: string | null;
  correlationId: string;
  payerName: string | null;
  payerAccountId: string | null;
  receiverName: string | null;
  receiverAccountId: string | null;
  description: string | null;
  isAnomaly: boolean;
  anomalyType: string | null;
}

export interface GroupTotalEntry {
  groupKey: string;
  groupValue: string;
  transactionCount: number;
  totalGrossAmount: number;
  totalFees: number;
  totalNetAmount: number;
}

export interface DetailedTransactionsResponse {
  transactions: TransactionEntry[];
  groupTotals?: GroupTotalEntry[];
  anomalySummary: {
    totalAnomalies: number;
    totalAnomalyValue: number;
    anomalyTypes: string[];
  };
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface DetailedTransactionsFilters {
  type?: string | string[];
  status?: string | string[];
  startDate?: string;
  endDate?: string;
  virtualAccountId?: string;
  minAmount?: number;
  maxAmount?: number;
  groupBy?: 'type' | 'status' | 'date' | 'account';
  page?: number;
  limit?: number;
}

// ============================================
// Helper: build query string from filters
// ============================================

function buildParams(filters: Record<string, any>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
    } else {
      params.set(key, String(value));
    }
  }
  return params;
}

// ============================================
// Hooks
// ============================================

export function useConsolidatedReport(filters: ConsolidatedReportFilters = {}) {
  return useQuery({
    queryKey: ['financial-reports', 'consolidated', filters],
    queryFn: async () => {
      const params = buildParams(filters);
      const { data } = await api.get(`/reports/financial/consolidated?${params}`);
      return data as ConsolidatedReportResponse;
    },
  });
}

export function usePerCustomerReport(filters: PerCustomerReportFilters = {}) {
  return useQuery({
    queryKey: ['financial-reports', 'per-customer', filters],
    queryFn: async () => {
      const params = buildParams(filters);
      const { data } = await api.get(`/reports/financial/per-customer?${params}`);
      return data as PerCustomerReportResponse;
    },
  });
}

export function useFeesMembershipReport(filters: FeesMembershipReportFilters = {}) {
  return useQuery({
    queryKey: ['financial-reports', 'fees-membership', filters],
    queryFn: async () => {
      const params = buildParams(filters);
      const { data } = await api.get(`/reports/financial/fees-membership?${params}`);
      return data as FeesMembershipReportResponse;
    },
  });
}

export function useDetailedTransactionsReport(filters: DetailedTransactionsFilters = {}) {
  return useQuery({
    queryKey: ['financial-reports', 'detailed-transactions', filters],
    queryFn: async () => {
      const params = buildParams(filters);
      const { data } = await api.get(`/reports/financial/detailed-transactions?${params}`);
      return data as DetailedTransactionsResponse;
    },
  });
}

export function useExportReport() {
  return useMutation({
    mutationFn: async (params: {
      reportType: 'consolidated' | 'per-customer' | 'fees-membership' | 'detailed-transactions';
      format: 'PDF' | 'CSV' | 'XLSX';
      filters?: Record<string, any>;
    }) => {
      const { data } = await api.post('/reports/financial/export', params, {
        responseType: 'blob',
      });

      const extensions: Record<string, string> = { PDF: 'pdf', CSV: 'csv', XLSX: 'xlsx' };
      const blob = new Blob([data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-${params.reportType}-${Date.now()}.${extensions[params.format]}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
